import { EmailLinkCallback } from "./pages/authentication/EmailLinkCallback";
import { SmsLinkCallback } from "./pages/authentication/SmsLinkCallback";
import { AuthProfilePrompt } from "./pages/authentication/components/AuthProfilePrompt";
import React, { Suspense } from "react";
import { ChakraProvider, extendTheme, Spinner, Center } from "@chakra-ui/react";

// SurgReady tenant is dark-only — clinical/medical UI aesthetic
const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },
});
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";

// SurgReady pages
import Homepage from "./pages/Homepage";
import About from "./pages/About";
import HowItWorks from "./pages/HowItWorks";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Providers from "./pages/Providers";
import CourseModule from "./pages/CourseModule";
import MemberHomepage from "./pages/MemberHomepage";
import MemberProfile from "./pages/MemberProfile";
import AllModulesOverview from "./pages/AllModulesOverview";
import { FloatingNavbar } from "./components/FloatingNavbar";
import { DocumentTitleManager } from "./components/DocumentTitleManager";
import ModuleTemplate from "./pages/ModuleTemplate";
import SleepModule from "./pages/modules/SleepModule";

// Client routes handled by clientsModuleConfig dynamic routing

// Bill-related imports (most routes handled by billsModuleConfig dynamic routing)
import BillChaserPage from "./pages/admin/billChaserPage";
import SubscribePage from "./pages/bills/SubscribePage";
import LoginPage from "./pages/authentication/LoginPage";
import SecurityPage from "./pages/security/SecurityPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";


// Product-related imports — products module not used on SurgReady tenant; deleted.

// Admin and dashboard imports
import AdminDashboard from "./pages/AdminDashboard";

// Cheatsheet imports
import CheatsheetIndex from "./pages/cheatsheets/CheatsheetIndex";
import SurgreadyPatientJourney from "./pages/cheatsheets/SurgreadyPatientJourney";
import SurgreadyAssessmentModule from "./pages/cheatsheets/SurgreadyAssessmentModule";

// Updates imports
import UpdatesIndex from "./pages/updates/UpdatesIndex";
import MarchVideoUpdate from "./pages/updates/MarchVideoUpdate";


// Test and development imports
import SendTestEmail from "./pages/test/SendTestEmail";
import TestUploadFile from "./pages/test/TestUploadFile";
import TestUploadEncryptedFile from "./pages/test/TestUploadEncryptedFile";
import TestClientCart from "./pages/test/TestClientCart";
// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ModulePageLayout } from "./components/ModulePageLayout";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";

// Module configurations - dynamic routing system
import lmModuleConfig from "./pages/lm/moduleConfig";
import governanceModuleConfig from "./pages/governance/moduleConfig";
import { governancePreviewForPath } from "./pages/governance/previews";

/**
 * A module may hand a signed-out visitor something to READ before the sign-in box (2026-09-16;
 * `seamless-page-auth-pattern.md` Part 3). The teaser is written by the module and public-safe — it
 * never imports a page component, so nothing protected reaches the browser. Without this registry the
 * modal opens over a blank page, which is what every tenant did until now.
 */
const ROUTE_PREVIEWS: Record<string, (path: string) => { title: string; blurb: string; node?: React.ReactNode } | undefined> = {
  governance: governancePreviewForPath,
};
import dashboardModuleConfig from "./pages/dashboard/moduleConfig";
import clientsModuleConfig from "./pages/clients/moduleConfig";
import siteEditorModuleConfig from "./pages/site-editor/moduleConfig";
import billsModuleConfig from "./pages/bills/moduleConfig";
import assessmentSnapshotModuleConfig from "./pages/assessment-snapshot/moduleConfig";
import emailsModuleConfig from "./pages/emails/moduleConfig";
import leadflowModuleConfig from "./pages/leadflow/moduleConfig";
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";

// Combine all module configurations

/**
 * Hand-written routes (the ones not owned by a module) get their tab name here — the standard's
 * `<Page> | <Site>`, with the site from brandConfig. Module routes need nothing: DocumentTitleManager
 * derives them from each moduleConfig. Never write document.title from a page (2026-09-16).
 */
const STATIC_ROUTE_TITLES: Record<string, string> = {
  "/": "",
  "/login": "Sign in",
  "/auth/sms-link": "Signing in",
  "/auth/email-link": "Signing in",
  "/security": "Security",
  "/onboarding": "Onboarding",
  "/terms": "Terms",
  "/privacy": "Privacy",
};
const modules = [
  governanceModuleConfig, // Groups that decide things together — motions, votes, work, treasury, a permanent record
  dashboardModuleConfig, // Post-payment learner hub (surgery countdown, to-dos, course access)
  assessmentSnapshotModuleConfig, // Health Assessment with radar chart tracking
  lmModuleConfig, // Learning Management System with course creation and enrollment
  clientsModuleConfig, // Client Management with CRM features
  siteEditorModuleConfig, // Site Editor with AI-powered website editing
  billsModuleConfig, // Billing & Subscriptions with dynamic routing
  emailsModuleConfig, // Emails module (Gmail skin) — permissions resolved from backend
  leadflowModuleConfig, // LeadFlow — lead capture (public /form/:slug) + CRM; permissions resolved from backend
  calendarsModuleConfig, // Calendars — events, booking, broadcasts, monthly goals; permissions resolved from backend
];

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color="blue.500" />
  </Center>
);


// Profile-related imports
import ViewProfile from "./pages/profile/ViewProfile";
import EditProfile from "./pages/profile/EditProfile";
import Settings from "./pages/profile/Settings";


import TrainingStart from "./pages/TrainingStart";

const AppContent = () => {
  const { getPermissions } = useModulePermissions();

  // Resolve backend permissions into module configs for FloatingNavbar
  const resolvedModules = React.useMemo(() => {
    return modules.map((module) => {
      const resolved: Record<string, string[]> = {};
      for (const level of ['view', 'create', 'edit', 'delete', 'admin', 'financial', 'review']) {
        const perms = getPermissions(module.id, level);
        if (perms.length > 0) resolved[level] = perms;
      }
      // Merge backend permissions into static config (backend overrides empty arrays)
      const mergedPermissions = { ...module.permissions };
      for (const [level, perms] of Object.entries(resolved)) {
        mergedPermissions[level] = perms;
      }
      return { ...module, permissions: mergedPermissions };
    });
  }, [getPermissions]);

  return (
    <>
      {/* Floating navigation menu — receives resolved modules with backend permissions */}
      <FloatingNavbar modules={resolvedModules} />

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <DocumentTitleManager modules={modules} staticTitles={STATIC_ROUTE_TITLES}>
      <Routes>
        <Route path="/auth/email-link" element={<EmailLinkCallback />} />
        <Route path="/auth/sms-link" element={<SmsLinkCallback />} />
        {/* SurgReady pages */}
        <Route path="/" element={<Homepage />} />
        <Route path="/training-start" element={<TrainingStart />} />
        <Route path="/about" element={<About />} />
        <Route path="/providers" element={<Providers />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* Benefits + FAQ pages removed (Sam, website feedback 27 Jul 2026): the FAQs are on the homepage now. */}
        <Route path="/benefits" element={<Navigate to="/" replace />} />
        <Route path="/faq" element={<Navigate to="/" replace />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Security & sign-in management — any logged-in user */}
        <Route path="/security" element={<ProtectedRoute requiredPermissions={[]}><SecurityPage /></ProtectedRoute>} />

        {/* Onboarding — staff with the ONBOARDING permission */}
        <Route path="/onboarding" element={<ProtectedRoute requiredPermissions={["ONBOARDING"]}><OnboardingPage /></ProtectedRoute>} />
        <Route path="/all-modules" element={<AllModulesOverview />} />
        <Route path="/course-module" element={<CourseModule />} />
        <Route path="/module/:moduleId" element={<ModuleTemplate />} />
        <Route path="/module/:moduleId/:pageNum" element={<ModuleTemplate />} />
        <Route path="/module/sleep" element={<SleepModule />} />
        <Route path="/member-homepage" element={<MemberHomepage />} />
        <Route path="/member-profile" element={<MemberProfile />} />



        {/* Client routes handled by clientsModuleConfig dynamic routing (with useLayout: true) */}

        {/* Website routes */}

        {/* Bills module uses dynamic routing via billsModuleConfig */}
        {/* Special bill routes not in the module */}
        <Route path="/bill-chaser" element={<BillChaserPage />} />
        <Route path="/subscribe/:slug" element={<SubscribePage />} />

        {/* Product routes removed — products module not used on SurgReady tenant. */}

        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* Updates routes */}
        <Route path="/updates" element={<UpdatesIndex />} />
        <Route path="/updates/march-2026-video" element={<MarchVideoUpdate />} />

        {/* Cheatsheet routes */}
        <Route path="/cheatsheets" element={<CheatsheetIndex />} />
        <Route path="/cheatsheets/surgready-patient-journey" element={<SurgreadyPatientJourney />} />
        <Route path="/cheatsheets/surgready-assessment-module" element={<SurgreadyAssessmentModule />} />

        {/* Profile routes */}
        <Route path="/profile" element={<ViewProfile />} />                        {/* USER: View profile details */}
        <Route path="/profile/edit" element={<EditProfile />} />                  {/* USER: Edit profile information */}
        <Route path="/profile/settings" element={<Settings />} />                  {/* USER: Manage profile settings */}
        {/*<Route path="/profile/shipping" element={<ShippingAddresses />} />        {/*  USER: Manage shipping addresses */}
        {/*<Route path="/profile/billing" element={<BillingDetails />} />            {/* USER: Manage billing details */}
        {/*  <Route path="/profile/payment-methods" element={<PaymentMethods />} />    {/* USER: Manage payment methods */}

        {/* Test routes */}
        <Route path="/test/sendemail" element={<SendTestEmail />} />
        <Route path="/test/upload" element={<TestUploadFile />} />
        <Route path="/test/upload-encrypted" element={<TestUploadEncryptedFile />} />
        <Route path="/test/cart" element={<TestClientCart />} />
        {/* Dynamic Module Routes — resolves permissionLevel from backend */}
        {modules.map((module) =>
          module.routes?.map((route) => {
            // Resolve permissions: explicit array > permissionLevel from backend > none
            let resolvedPermissions: string[] = [];
            if (route.permissions && route.permissions.length > 0) {
              resolvedPermissions = route.permissions;
            } else if (route.permissionLevel) {
              resolvedPermissions = getPermissions(module.id, route.permissionLevel);
            }

            // Seamless page auth (vault/how-to/seamless-page-auth-pattern.md):
            // a route with a permissionLevel MUST be gated even when logged out.
            // Backend permissions can't resolve without an auth token, so
            // resolvedPermissions is [] until after login — the old
            // `resolvedPermissions.length > 0` check let logged-out visitors
            // bypass ProtectedRoute entirely (e.g. /lm, /lm/admin rendered with
            // no login modal). Gate on permissionLevel OR explicit permissions;
            // only truly-public routes (no permissionLevel, permissions:[] /
            // public:true) render ungated. requiredPermissions stays
            // resolvedPermissions: [] just requires auth; post-login this block
            // recomputes with the real perms and enforces them.
            const isProtected = !route.public && (resolvedPermissions.length > 0 || !!route.permissionLevel);
            const pageComponent = isProtected ? (
              <ProtectedRoute requiredPermissions={resolvedPermissions} preview={ROUTE_PREVIEWS[module.id]?.(route.path)}>
                <Suspense fallback={<LoadingFallback />}>
                  <route.component />
                </Suspense>
              </ProtectedRoute>
            ) : (
              <Suspense fallback={<LoadingFallback />}>
                <route.component />
              </Suspense>
            );

            // If useLayout is true, wrap with ModulePageLayout (auto Navbar + Breadcrumb + Container + Footer)
            const content = route.useLayout ? (
              <ModulePageLayout
                moduleConfig={module}
                layoutOptions={route.layoutOptions}
              >
                {pageComponent}
              </ModulePageLayout>
            ) : pageComponent;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={content}
              />
            );
          })
        )}

      </Routes>
      </DocumentTitleManager>
    </>
  );
};

export const App = () => (
  <Router>
    <ApolloProvider client={apolloClient}>
      <IpfsGatewayLoader>
        <AuthProvider>
          <ModulePermissionsProvider>
            <ChakraProvider theme={theme}>
              <AuthProfilePrompt />
                    <AppContent />
            </ChakraProvider>
          </ModulePermissionsProvider>
        </AuthProvider>
      </IpfsGatewayLoader>
    </ApolloProvider>
  </Router>
);
